/* eslint-disable array-element-newline */
/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import {LitElement, html, nothing} from 'lit';
import {createRef, ref} from 'lit/directives/ref.js';
import {classMap} from 'lit/directives/class-map.js';
import {defineCustomElement} from 'extensions/utils';
import styles from './setting-card.component.scss?as=stylesheet';
import {PRODUCT_ATTRIBUTES_PARAM_KEY, SETTING_NOT_FOUND_IMAGE} from '../../data/constants';

class SettingCard extends MobxReactionUpdate(LitElement) {

	static get styles () {
		return [styles];
	}

	mainImageElement = createRef();

	static get properties () {
		return {
			currentImage: {
				type: String
			},
			imajizeActive: {
				type: Boolean
			},
			mainImage: {
				type: String
			},
			result: {
				type: Object
			},
			sourceLoaded: {
				state: true,
				type: Boolean
			},
			store: {
				state: true,
				type: Object
			}
		};
	}

	constructor () {
		super();

		this.currentImage = '';
		this.sourceLoaded = false;
		this.imajizeActive = 0;
		this.noImageFound = SETTING_NOT_FOUND_IMAGE;
		this.hasHovered = 0;
		this.thumbnailSlider = null;
	}

	get mainImage () {
		return this.store?.diamond?.product?.id ? this.result?.custom?.settingUnderlayImage : this.result?.custom?.settingImage;
	}

	imageContainerChanged (container) {
		if (!container) {
			return;
		}

		const observer = new MutationObserver((changes) => {
			changes.forEach(change => {
				if (change.attributeName.includes('src')) {
					this.sourceLoaded = container.complete;
				}
			});
		});

		observer.observe(container, {attributes: true});
	}

	updated (changedProperties) {
		super.updated();

		if (changedProperties.has('result') && changedProperties.result?.value?.id !== this.result?.id) {
			this.setCurrentImage(this.mainImage);
		}

		this.checkCompares();
	}

	checkCompares () {
		const compareCheckboxes = this.shadowRoot?.querySelectorAll('.setting-card--compare-input');

		for (const compareCheckbox of compareCheckboxes) {
			compareCheckbox.checked = Boolean(this.compareChecked);
		}
	}

	toggleCompareListProduct () {
		this.store.setting.toggleCompareListProduct(this.result);
	}

	render () {
		const classes = {
			'setting-card': true,
			'setting-card--image-loaded': this.sourceLoaded,
			'setting-card--no-touch': !this.store.isTouch
		};

		const compareClasses = {
			'setting-card--compare': true,
			'setting-card--compare-checked': this.compareChecked
		};

		return html`
			<div class="${classMap(classes)}">
				<div class="setting-card--inner">
					<div class="setting-card--top">
						<a href="${this.gotoSettingHref}" class="setting-card--image">
							<div class="setting-card--image-loader"></div>
							<div class="setting-card--image-inner">
								${this.renderImage()}
							</div>
							${this.renderSelected()}
						</a>
					</div>
					<div class="setting-card--middle">
						${this.renderSwatches()}
						<div class="setting-card--brand">${this.result?.attributes?.brand}</div>
						<a href="${this.gotoSettingHref}" class="setting-card--name">${this.name}</a>
					</div>
					<div class="setting-card--bottom">
						<div class="setting-card--pricing">
							${this.renderPricing()}
						</div>
						<label class="${classMap(compareClasses)}" for="js-compare-${this.result.id}" aria-label="Add ${this.name} to Compare">
							<input class="setting-card--compare-input" @change="${this.toggleCompareListProduct}" type="checkbox" id="js-compare-${this.result.id}" ?checked="${this.compareChecked}" />
							<span class="setting-card--compare-text">Add to Compare</span>
						</label>
					</div>
				</div>
				${this.renderFooter()}
			</div>
		`;
	}

	renderImage () {
		if (this.imajizeActive) {
			return html`
				<div class="setting-card--video-wrap">
					<iframe src="https://embed.imajize.com/${this.result?.attributes?.imajize_idenitifer}" title="${this.name}" frameborder="0" scrolling="no" class="setting-card--video" allowfullscreen @load="${this.handleSourceLoad}"></iframe>
				</div>
			`;
		}

		let diamondOutput = nothing;
		if (this.store?.diamond?.product?.id && this.isUnderlayImage(this.currentImage) && this.sourceLoaded) {
			diamondOutput = html`
				<rb-diamond-overlay metal="${this.metalType}" ?halo="${this.result?.attributes?.halo_ring_builder}" vendor="${this.result?.attributes?.vendor}" product-code="${this.result?.id}"></rb-diamond-overlay>
			`;
		}

		return html`
			<div class="setting-card--image-wrap">
				<img src="${this.currentImage}" alt="${this.name}" @load="${this.handleSourceLoad}" @error="${this.handleImageError}" height="350" width="350" loading="lazy"  ${ref(this.mainImageElement)} ${ref(this.imageContainerChanged)} />
				${diamondOutput}
			</div>
		`;
	}

	renderSelected () {
		if (!this.store?.setting?.product?.id || this.store?.setting?.product?.id !== this.result?.id) {
			return nothing;
		}

		return html`
			<span class="setting-card--selected">Selected</span>
		`;
	}

	renderSwatches () {
		if (this.result?.custom?.swatches?.length <= 1) {
			return nothing;
		}

		return html`
			<div class="setting-card--swatches">
				${this.result.custom.swatches.map(swatch => this.renderSwatch(swatch))}
			</div>
		`;
	}

	renderSwatch (swatch) {
		const classes = {
			'setting-card--swatch': true,
			'setting-card--swatch--active': swatch.code === this.result?.custom?.currentSwatch?.code
		};

		return html`
			<button class="${classMap(classes)}" @click="${() => this.handleSwatchClick(swatch)}">
				<img src="${swatch?.swatch_image}" class="setting-card--swatch-source" loading="lazy" />
			</button>
		`;
	}

	handleSwatchClick (swatch) {
		if (swatch.code === this.result?.custom?.currentSwatch?.code) {
			return;
		}

		this.result.custom.currentSwatch = swatch;
		this.result.custom.settingImage = this.result.custom.currentSwatch.settingImage;
		this.result.custom.settingImageThumbnail = this.result.custom.currentSwatch.settingImageThumbnail;
		this.result.custom.settingUnderlayImage = this.result.custom.currentSwatch.settingUnderlayImage;
		this.result.custom.settingUnderlayImageThumbnail = this.result.custom.currentSwatch.settingUnderlayImageThumbnail;
		this.result.custom.images = [...this.result.custom.currentSwatch.images];

		this.setCurrentImage(this.store?.diamond?.product?.id ? this.result?.custom?.settingUnderlayImage : this.result?.custom?.settingImage);

		this.updatePricing();
	}

	async updatePricing () {
		if (!this.result?.custom?.currentSwatch?.code) {
			return;
		}

		window.variantLivePricingCache = window.variantLivePricingCache || [];

		if (!window.variantLivePricingCache[this.result.custom.currentSwatch.code]) {
			const data = new FormData();
			data.append('codes', [this.result.custom.currentSwatch.code]);

			const request = await fetch(`${window?.mivaJS?.urls['ss-pricing']}`, {
				body: data,
				method: 'POST'
			});

			if (!request.ok) {
				return;
			}

			const response = await request.json();

			// eslint-disable-next-line require-atomic-updates
			window.variantLivePricingCache[this.result.custom.currentSwatch.code] = response[this.result.custom.currentSwatch.code];
		}

		this.updatePrice(window.variantLivePricingCache[this.result.custom.currentSwatch.code]);
	}

	updatePrice (data) {
		if (data?.sale_price && this.result?.price) {
			this.result.price = data.sale_price;
		}

		if (data?.price && this.result?.retail) {
			this.result.retail = data.price;
		}
	}

	renderPricing () {
		return html`
			<div class="setting-card--price">${this.result?.formattedPrice}</div>
		`;
	}

	// eslint-disable-next-line max-lines-per-function
	renderFooter () {
		if (!this.result?.custom?.images?.length && !this.result?.attributes?.imajize_idenitifer) {
			return nothing;
		}

		let count = 0;

		const imageOutput = [];

		for (const image of this.result?.custom?.images) {
			if (image.type === 'rb2' && !this.store?.diamond?.product?.id) {
				continue;
			}

			const classes = {
				'is-selected': this.currentImage === image.image && !this.imajizeActive,
				'setting-card--thumbnail': true
			};

			if (this.store?.diamond?.product?.id && this.isUnderlayImage(image.image)) {
				imageOutput.push(html`
					<span class="${classMap(classes)}" @click="${() => this.setCurrentImage(image.image)}" data-type="${image.type}">
						<rb-diamond-overlay metal="${this.metalType}" ?halo="${this.result?.attributes?.halo_ring_builder}" vendor="${this.result?.attributes?.vendor}" product-code="${this.result?.id}"></rb-diamond-overlay>
						<img src="${image.thumbnail}" loading="lazy"/>
					</span>
				`);
			} else {
				if (this.store?.diamond?.product?.id && image.type === 'main') {
					continue;
				}

				imageOutput.push(html`
					<span class="${classMap(classes)}" @click="${() => this.setCurrentImage(image.image)}" data-type="${image.type}">
						<img src="${image.thumbnail}" loading="lazy" />
					</span>
				`);
			}

			count++;
		}

		if (this.result?.attributes?.imajize_idenitifer) {
			const imajizeClasses = {
				'is-selected': this.imajizeActive,
				'setting-card--thumbnail': true,
				'setting-card--video-thumbnail': true
			};

			imageOutput.push(html`
				<span class="${classMap(imajizeClasses)}" @click="${() => this.setImajizeActive(1)}" aria-label="Video" >
					<span class="icon-svg-play setting-card--thumbnail-icon" aria-hidden="true"></span>
				</span>
			`);

			count++;
		}

		if (count <= 1) {
			return nothing;
		}

		return html`
			<div class="setting-card--hover">
				<div class="setting-card--hover-inner">
					<div class="setting-card--thumbnails">
						${imageOutput}
					</div>
				</div>
			</div>
		`;
	}

	async setCurrentImage (image) {
		await this.updateComplete;

		this.currentImage = image;
		this.setImajizeActive(0);
	}

	setImajizeActive (active) {
		this.imajizeActive = active;
	}

	isUnderlayImage (image) {
		return this.result?.custom?.settingUnderlayImage ? image === this.result?.custom?.settingUnderlayImage : image === this.result?.custom?.settingImage;
	}

	get compareChecked () {
		return Boolean(this.store?.setting?.compareList?.find(compareProduct => compareProduct?.id === this.result?.id));
	}

	get gotoSettingHref () {
		const url = new URL(this.url, window.location.origin);
		for (let [key, value] of this.store?.urlSearchParams) {
			if (key.startsWith(PRODUCT_ATTRIBUTES_PARAM_KEY)) {
				continue;
			}
			url.searchParams.set(key, value);
		}
		url.searchParams.set('setting', '');

		return url.toString();
	}

	get url () {
		if (!this.result?.custom?.currentSwatch?.variant_id) {
			return this.result?.url;
		}

		const url = new URL(this.result?.url, window.location.origin);
		url.searchParams.set('variant_id', this.result.custom.currentSwatch.variant_id);

		return url.toString();
	}

	get name () {
		if (!this.result?.custom?.currentSwatch?.product_name) {
			return this.result?.title;
		}

		return this.result?.custom?.currentSwatch?.product_name;
	}

	handleSourceLoad () {
		this.sourceLoaded = true;
	}

	handleImageError () {
		this.currentImage = SETTING_NOT_FOUND_IMAGE;
	}

	get metalType () {
		if (this.result?.custom?.currentSwatch?.swatch_metal) {
			return this.result?.custom?.currentSwatch?.swatch_metal;
		}

		return this.result?.attributes?.rs_metal_type;
	}
};

defineCustomElement('rb-setting-card', SettingCard);
